import { FC, useCallback, useEffect, useState } from 'react';
import { Box, FormHelperText, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import debounce from 'lodash/debounce';
import Text from '@/components/text';
import TextArea from '@/components/form/textarea';
import HighlightText from '@/components/highlight-text';
import Loader from '@/components/loader';
import { REGEX_TEST_TEXT_MAX_LENGTH } from '@/lib/regex-helpers';
import { RegexHighlightResult, runRegexHighlightSafely } from './helpers';

interface Props {
  regexPattern: string;
  initialTestPatterns?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  contentArea: {
    resize: 'none',
    borderRadius: 4,
    backgroundColor: theme.palette.subtleGray,
    border: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    fontFamily: 'monospace',
    fontSize: theme.typography.fontSize,
    '& .MuiTypography-root': {
      fontFamily: 'monospace',
      fontSize: theme.typography.fontSize,
      display: 'inline',
    },
    overflow: 'auto',
  },
}));

const MatchPreview: FC<Props> = ({ regexPattern, initialTestPatterns }) => {
  const styles = useStyles();
  const [testPatterns, setTestPatterns] = useState(
    initialTestPatterns ||
      'Test your pattern <INSERT_VALUE> with a sentence or two of example text to represent a user prompt.'
  );
  const [highlightResult, setHighlightResult] = useState<RegexHighlightResult | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHighlight = useCallback(
    debounce((text: string, pattern: string) => {
      setHighlightResult(runRegexHighlightSafely(text, pattern));
    }, 250),
    []
  );

  useEffect(() => {
    debouncedHighlight(testPatterns, regexPattern);
    return () => debouncedHighlight.cancel();
  }, [testPatterns, regexPattern, debouncedHighlight]);

  const showHighlightLoader = !!testPatterns && !highlightResult;

  return (
    <Stack direction="row" width="100%" gap={2} flexWrap="wrap">
      <Stack flex={1}>
        <Text bold size="small">
          Test Patterns
        </Text>
        <TextArea
          name="testPatterns"
          value={testPatterns}
          onChange={setTestPatterns}
          minRows={4}
          maxRows={4}
          maxChars={REGEX_TEST_TEXT_MAX_LENGTH}
          label="Enter sample text..."
        />
      </Stack>
      <Stack flex={1}>
        <Text bold size="small">
          Match Preview
        </Text>
        <Box height={90} p={1} mt={2} className={styles.contentArea}>
          {showHighlightLoader ? (
            <Loader />
          ) : (
            highlightResult?.segments.map(({ isMatch, text }, index) =>
              isMatch ? (
                <HighlightText key={`${text}-${index}-match`} color="success" isHighlighted inline>
                  {text}
                </HighlightText>
              ) : (
                <Text key={`${text}-${index}-no-match`} inline>
                  {text}
                </Text>
              )
            )
          )}
        </Box>
        <Box mt={0.5} alignSelf="flex-end">
          <Text size="x-small" color="grey">
            Matched patterns are highlighted above
          </Text>
        </Box>
        {highlightResult?.reason && <FormHelperText error>{highlightResult.reason}</FormHelperText>}
      </Stack>
    </Stack>
  );
};

export default MatchPreview;
